import { Clock4 } from "lucide-react";
import { useState, useEffect } from "react";
import dayjs from 'dayjs';

export default function DrawResults({ times = [], gameSettings, onSelectedDrawTime, isDrawTimeCutoff = [], activeDrawTime, cutOffTimes }) {
  const getTimeNow = dayjs().format('HH:mm:ss');
  const allDrawTimes = gameSettings?.otherSettings?.drawTime || [];
  const gameId = gameSettings?.id;

  // Function to check if a draw time is past cutoff
  const isTimeCutoff = (time) => {
    if (!allDrawTimes || !isDrawTimeCutoff || !isDrawTimeCutoff.length) return false;
    const index = allDrawTimes.indexOf(time);
    return index !== -1 && isDrawTimeCutoff[index];
  };

  // Ensure times is an array before using reduce
  const resultsMap = Array.isArray(times) ? times.reduce((acc, result) => {
    if (!result || !result.time) return acc;
    
    if (result.gameId === gameId) {
      const formattedTime = dayjs(`2024-01-01 ${result.time}`).format('h:mm A');
      if (result.numbers) {
        acc[formattedTime] = result.numbers;
      }
    }
    return acc;
  }, {}) : {};

  const findNextTime = () => {
    if (!allDrawTimes || allDrawTimes.length === 0) return null;

    const currentTimeMinutes = dayjs().hour() * 60 + dayjs().minute();
    
    // Find the next available draw time that's not past cutoff
    const nextTime = allDrawTimes.find(time => {
      const [hours, minutes] = time.split(':').map(Number);
      const drawTimeMinutes = hours * 60 + minutes;
      return drawTimeMinutes > currentTimeMinutes && !isTimeCutoff(time);
    });

    // If all times are past cutoff, return the first one anyway
    return nextTime || allDrawTimes[0];
  };

  // Initialize with activeDrawTime if provided, otherwise find next time
  const [selectedType, setSelectedType] = useState(activeDrawTime || findNextTime());

  useEffect(() => {
    if (activeDrawTime) {
      setSelectedType(activeDrawTime);
    } else {
      setSelectedType(findNextTime());
    }
  }, [allDrawTimes, isDrawTimeCutoff, activeDrawTime]);

  const chooseTime = (time) => {
    // Only allow selection if not past cutoff
    if (!isTimeCutoff(time)) {
      setSelectedType(time);
      if (typeof onSelectedDrawTime === 'function') {
        onSelectedDrawTime(time);
      }
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const fullTime = time.length === 5 ? `${time}:00` : time;
    return dayjs(`2024-01-01 ${fullTime}`).format('h:mm A');
  };

  const emptyNumbers = [
    { num: "0", color: "[#0E64D1]", gradient: "" },
    { num: "0", color: "yellow-400", gradient: "" },
    { num: "0", color: "red-600", gradient: "" }
  ];

  return (
    <>
      <div className="grid grid-cols-3 gap-x-3 mb-6 gap-y-5">
        {(allDrawTimes || []).map((time) => {
          const formattedTime = formatTime(time);
          const resultNumbers = resultsMap[formattedTime] || emptyNumbers;
          const isDisabled = isTimeCutoff(time);
          
          return (
            <div key={time} className="flex flex-col gap-1.5">
              <button
                onClick={() => chooseTime(time)}
                disabled={isDisabled}
                className={`flex gap-4 max-xs:gap-2 items-center flex-1 rounded-full border font-bold p-1 text-sm ${
                  selectedType === time && !isDisabled
                    ? "border-yellow-200 text-yellow-200 bg-gradient-to-b from-[#FF0000] via-[#6D1010] to-[#FF0000]"
                    : isDisabled 
                      ? "border-gray-500 text-gray-400 bg-gradient-to-b from-[#4A4A4A] via-[#2E2E2E] to-[#4A4A4A] opacity-50 cursor-not-allowed"
                      : "border-white text-white bg-gradient-to-b from-[#0E64D1] via-[#1E429A] to-[#0E64D1]"
                }`} 
              >
                <Clock4 className="w-4 h-4 ms-1" />
                {formattedTime}
              </button>
              <div className="text-xs text-gray-400 text-center">Results</div>
              <div className="flex justify-center gap-1.5">
                {resultNumbers.map((digit, index) => (
                  <div
                    key={`${time}-${index}`}
                    className="relative flex items-center justify-center w-6 h-6"
                  >
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        transform: "rotate(25deg)",
                        background: digit.gradient,
                      }}
                    />
                    <div
                      className={`relative flex items-center justify-center w-[22px] h-[22px] rounded-full bg-${digit.color} text-white text-sm font-medium`}
                    >
                      {digit.num}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
